import { FrontendApplication, FrontendApplicationContribution } from '@theia/core/lib/browser';
import { Disposable, DisposableCollection } from '@theia/core/lib/common/disposable';
import { syncAppShellInteractionLock } from '@theia-lved-core/browser/shell/editor-chrome-lock';
import { injectable } from '@theia/core/shared/inversify';

const LICENSE_GATE_BODY_CLASS: string = 'lvgl-license-gate-active';

/**
 * While no valid Pro license is active, disables pointer interaction with the main Theia shell.
 * The license overlay host and `#lvgl-top-actions` stay interactive (see `license-shell-gate.css`).
 */
@injectable()
export class LicenseShellGateContribution implements FrontendApplicationContribution {
    protected readonly toDispose = new DisposableCollection();

    async onStart(_app: FrontendApplication): Promise<void> {
        try {
            const { useAuthStore, useLicenseStore, hasValidLicenseSelection } = await import('@lved/ui');
            const syncGate = (): void => {
                const isAuthenticated: boolean = useAuthStore.getState().isAuthenticated === true;
                const hasValidLicense: boolean = hasValidLicenseSelection(useLicenseStore.getState(), Date.now());
                this.setGateActive(!isAuthenticated || !hasValidLicense);
            };
            syncGate();
            const unsubscriptions: Array<() => void> = [
                useAuthStore.subscribe(syncGate),
                useLicenseStore.subscribe(syncGate)
            ];
            this.toDispose.push(
                Disposable.create(() => {
                    for (const unsubscribe of unsubscriptions) unsubscribe();
                })
            );
        } catch {
            this.setGateActive(true);
        }
    }

    onStop(_app: FrontendApplication): void {
        this.toDispose.dispose();
        this.setGateActive(false);
    }

    protected setGateActive(active: boolean): void {
        document.body.classList.toggle(LICENSE_GATE_BODY_CLASS, active);
        syncAppShellInteractionLock();
    }
}
